"use client";

import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { useMemo, useEffect, useState } from "react";

const DashboardChatWidget = dynamic(
  () => import("@components/chat/DashboardChatWidget"),
  { ssr: false }
);

const PUBLIC_ROUTE_PREFIXES = [
  "/services-booking",
  "/services-wizard",
  "/services-portal",
  "/services-manage",
  "/services-review",
  "/connect-and-join",
  "/org-or-services",
  "/auth/oauth",
  "/auth/oauth2",
  "/auth/saml",
];

function getPageContext(pathname: string | null) {
  const page = pathname || "";
  const context: Record<string, unknown> = { page };

  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);

  if (page.includes("/bookings") || page.includes("/appointments")) {
    context.pageName = "Bookings";
    const dateParam = params.get("date");
    if (dateParam) context.date = dateParam;
  } else if (page.includes("/customers") || page.includes("/clients")) {
    context.pageName = "Customers";
    const match = page.match(/\/customers\/(\d+)/);
    if (match) context.customerId = parseInt(match[1], 10);
  } else if (page.includes("/services") && !page.includes("/services-")) {
    context.pageName = "Services";
    const match = page.match(/\/services\/(\d+)/);
    if (match) context.serviceId = match[1];
  } else if (page.includes("/staff") || page.includes("/employees")) {
    context.pageName = "Staff";
    const match = page.match(/\/staff\/(\d+)/);
    if (match) context.staffId = parseInt(match[1], 10);
  } else if (page.includes("/products") || page.includes("/inventory")) {
    context.pageName = "Products & Inventory";
  } else if (page.includes("/insights") || page.includes("/analytics")) {
    context.pageName = "Insights";
  } else if (page.includes("/event-types")) {
    context.pageName = "Event Types";
  } else if (page.includes("/availability")) {
    context.pageName = "Availability";
  } else if (page.includes("/teams")) {
    context.pageName = "Teams";
  } else if (page.includes("/routing")) {
    context.pageName = "Routing";
  } else if (page.includes("/workflows")) {
    context.pageName = "Workflows";
  } else if (page.includes("/settings")) {
    context.pageName = "Settings";
  } else if (page.includes("/services-dashboard")) {
    context.pageName = "Services Dashboard";
  }

  const locationId = params.get("locationId");
  if (locationId) context.locationId = parseInt(locationId, 10);

  return context;
}

export default function ChatWidgetWrapper() {
  const { status } = useSession();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const pageContext = useMemo(
    () => mounted ? getPageContext(pathname) : { page: pathname || "" },
    [pathname, mounted]
  );

  if (status !== "authenticated") return null;

  const isPublicPage = PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname?.startsWith(prefix));
  if (isPublicPage) return null;

  return <DashboardChatWidget context={pageContext} />;
}
